import { css, cx } from "@emotion/css";
import { Colors } from "@analyticsMD/quil";
import { Button, Typography, Box, Collapse, Link } from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import SendIcon from "@mui/icons-material/Send";
import {
  styles as commonStyles,
  sxStyles as commonSxStyles,
} from "./common-styles";
import { FC, useState, useContext, useEffect } from "react";
import SectionGrid from "../section-grid";
import Configuration from "../../types/Configuration";
import { blueGrey, grey } from "@mui/material/colors";
import NoteCreate from "../notes/note-create";
import APIContext from "../../contexts/APIContext";
import { postPatientNote } from "../../api/endpoints";
import { useMutation, useQueryClient } from "react-query";
import PatientNote from "../../types/PatientNote";
import NoteCard from "../notes/note-card";
import NotesDrawer from "../notes/notes-drawer";

const styles = {
  ...commonStyles,
  insightsSection: css`
    background-color: ${Colors.white};
  `,
  expandCollapseIcon: css`
    color: ${Colors.grey_40};
  `,
  emptyMsg: css`
    margin-bottom: 1rem;
    color: ${Colors.grey_40};
    width: 50%;
    text-align: center;
    font-size: 0.9rem;
  `,
  emptyNotes: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 2rem;
    margin-bottom: 2rem;
  `,
  noteCards: css`
    margin-top: 1rem;
  `,
  viewOlderNotes: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 1rem;
  `,
};

const sxStyles = {
  ...commonSxStyles,
  icon: {
    padding: "0.2rem",
    color: Colors.white,
    backgroundColor: blueGrey[300],
    borderRadius: "0.25rem",
    fontSize: "1rem",
    marginRight: "0.5rem",
  },
  emptyStateIcon: {
    fontSize: "2.5rem",
    color: grey[300],
    marginBottom: "1rem",
  },
  addNoteButton: {
    textTransform: "none",
    marginTop: "0.7rem",
    padding: "0.3rem 0.6rem",
    fontSize: "0.812rem",
    fontWeight: "500",
  },
  archivedLink: {
    display: "flex",
    alignItems: "center",
    color: Colors.grey_40,
    fontSize: "0.875rem",
    cursor: "pointer",
    "&:hover": {
      color: Colors.grey_60,
    },
  },
  archivedIcon: {
    fontSize: "1rem",
    marginRight: "0.25rem",
  },
};

interface NotesSectionProps {
  configuration?: Configuration;
}

const NotesSection: FC<NotesSectionProps> = () => {
  const [creatingNote, setCreatingNote] = useState<boolean>(false);
  const [PatientNotes, setPatientNotes] = useState<PatientNote[] | null>(null);
  const [notesDrawerOpen, setNotesDrawerOpen] = useState<boolean>(false);
  const { patientID, patientNotesQuery, isViewOnly } = useContext(APIContext);
  const [showSection, setShowSection] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const queryClient = useQueryClient();
  const { mutate: createNote } = useMutation(postPatientNote, {
    onSuccess: () => {
      queryClient.invalidateQueries("notes");
    },
  });

  const handleCreateNote = (text: string) => {
    setCreatingNote(false);
    if (text && patientID) {
      setPatientNotes((prev) => [
        {
          id: 0,
          patient: patientID,
          content: text,
          created_by_name: urlParams.get("emr_user_full_name"),
          created_by_email: urlParams.get("emr_user_email"),
          qv_created_ts: new Date().toISOString(),
        } as PatientNote,
        ...prev,
      ]);
      createNote({
        patient: patientID,
        content: text,
        created_by_email: urlParams.get("emr_user_email"),
      });
    }
  };

  const handleNoteDeleted = (id: number) => {
    setPatientNotes((prev) => prev?.filter((note) => note.id !== id));
  };

  const renderNotesSection = () => {
    return (
      <>
        {/* {  ---- Create notes button ----- */}
        {!isViewOnly && !creatingNote && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={sxStyles.addNoteButton}
            onClick={() => {
              setCreatingNote(true);
            }}
          >
            Create
          </Button>
        )}
        {/* {  ---- Create notes text area ----- */}
        {!isViewOnly && creatingNote && (
          <NoteCreate
            placeholder="Type your note here..."
            button={
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                sx={sxStyles.addNoteButton}
              >
                Post
              </Button>
            }
            onSubmit={handleCreateNote}
          />
        )}
        {/* {  ---- Empty notes ----- */}
        {!PatientNotes?.length && (
          <Box className={styles.emptyNotes}>
            <InsertDriveFileOutlinedIcon sx={sxStyles.emptyStateIcon} />
            <span className={styles.emptyMsg}>No notes found</span>
          </Box>
        )}
        {/* {  ---- Note cards ----- */}
        {
          <Box className={styles.noteCards}>
            {Boolean(PatientNotes?.length) && (
              <NoteCard
                note={PatientNotes[0]}
                onNoteDeleted={handleNoteDeleted}
                readOnly={isViewOnly}
              />
            )}
          </Box>
        }
        {/* {  ---- View Older Notes ----- */}
        {PatientNotes?.length > 1 && (
          <Box className={styles.viewOlderNotes}>
            <Typography variant="caption" sx={{ fontSize: "0.875rem" }}>
              You&apos;re looking at the latest note.
            </Typography>
            <Link
              component="button"
              onClick={() => setNotesDrawerOpen(true)}
              underline="hover"
              sx={{ fontSize: "0.875rem" }}
            >
              {`View all notes (${PatientNotes?.length})`}
            </Link>
          </Box>
        )}
        {/* {  ---- Notes Drawer ----- */}
        <NotesDrawer
          open={notesDrawerOpen}
          patientNotes={PatientNotes}
          onNoteDeleted={handleNoteDeleted}
          onClose={() => setNotesDrawerOpen(false)}
          readOnly={isViewOnly}
        />
      </>
    );
  };

  useEffect(() => {
    if (patientNotesQuery.isSuccess) {
      setPatientNotes(patientNotesQuery.data);
    }
  }, [patientNotesQuery.isSuccess, patientNotesQuery.data]);

  return (
    <Box className={cx(styles.roundedSection, styles.insightsSection)}>
      <Box
        sx={sxStyles.headingWrapper}
        onClick={() => setShowSection(!showSection)}
      >
        <Typography sx={sxStyles.sectionHeading}>
          <AssignmentOutlinedIcon sx={sxStyles.icon} />
          <span>{`Notes (${PatientNotes?.length || 0})`}</span>
        </Typography>
        {!showSection && (
          <ExpandMoreIcon className={styles.expandCollapseIcon} />
        )}
        {showSection && (
          <ExpandLessIcon className={styles.expandCollapseIcon} />
        )}
      </Box>

      <Collapse in={showSection} unmountOnExit mountOnEnter>
        <SectionGrid>{renderNotesSection()}</SectionGrid>
      </Collapse>
    </Box>
  );
};

export default NotesSection;
